/* global google */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {IntlShape, useIntl} from 'react-intl';
import cn from 'classnames';

import {useConfiguration, useLocalesMgr, useNotify} from 'coreHooks';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import {
	STORE_LOCATOR_DEFAULT_DISTANCE_KM,
	STORE_LOCATOR_MAP_CONFIG,
	STORE_LOCATOR_ZOOM_LOCATION,
} from 'brand/app/pages/store-locator/config';
import {useStoreLocatorContext} from 'brand/app/pages/store-locator/context/StoreLocatorContext';
import messages from 'brand/app/pages/store-locator/messages';
import InfoWindowWrapper from 'brand/app/pages/store-locator/Subcomponents/InfoWindowWrapper';
import Marker, {IStoreMarker} from 'brand/app/pages/store-locator/Subcomponents/Marker';
import {GoogleMapsLibraryStatus} from 'brandComponents/GoogleMapsWrapper';

import {IStoreLocatorMapProps} from './IStoreLocatorMap';

// eslint-disable-next-line no-restricted-imports
import './StoreLocatorMap.scss';

const StoreLocatorMap = ({searchInputRef, visible}: IStoreLocatorMapProps) => {
	const intl: IntlShape = useIntl();
	const localesMgr = useLocalesMgr();
	const currentLanguageCode = localesMgr.getCurrentLanguageCode();
	const {center, stores, setStores, setSelectedStoreMarker, map, setMap, libraryStatus} =
		useStoreLocatorContext();
	const ref = useRef();
	const {managerRepository} = useGeneralProvider();
	const sitePreferences = useConfiguration('sitePreferences');
	const mapStyle = sitePreferences.storeLocatorGoogleMapsStyle as google.maps.MapTypeStyle[];
	const [autoComplete, setAutoComplete] = useState<google.maps.places.Autocomplete>(null);
	const [reloadStores, setReloadStores] = useState(false);
	const [placeChanged, setPlaceChanged] = useState(false);
	const notify = useNotify();

	const mapConfig = Object.assign({}, STORE_LOCATOR_MAP_CONFIG, {
		center: center,
		styles: mapStyle,
	});

	const handleMarkerClick = useCallback(
		(selectedStore: IStoreMarker) => {
			if (!map) {
				return;
			}

			setSelectedStoreMarker(null);
			setSelectedStoreMarker(selectedStore);
		},
		[map],
	);

	const buildPayload = (data) => {
		const bounds = map.getBounds();
		const start = bounds?.getNorthEast();
		// Adding a 10% tolerance for cover edges
		const distStart = start
			? google.maps.geometry.spherical.computeDistanceBetween(map.getCenter(), {
				lat: start.lat(),
				lng: start.lng(),
			  }) * 1.10 / 1000.0
			: null;

		return {
			c_latitude: data.lat,
			c_longitude: data.lng,
			c_distance: Math.max(Math.floor(distStart), STORE_LOCATOR_DEFAULT_DISTANCE_KM),
		};
	};

	const getStores = (data) => {
		const payload = buildPayload(data);

		// Fetch all if planisphere view
		if (map.getZoom() < 3) {
			payload.distance = null;
		}

		managerRepository
			.getCommerceApi()
			.customShopperStores
			.getStores({parameters: {...payload}})
			.then((res) => {
				const results =
					res?.stores?.filter((store) =>
						map.getBounds().contains({
							lat: store.latitude,
							lng: store.longitude,
						}),
					) || [];

				setStores(results);
				setReloadStores(false);
				setPlaceChanged(false);

				if (placeChanged && results.length === 0) {
					notify.error(intl.formatMessage(messages.noResults));
				}
			});
	};

	useEffect(() => {
		if (libraryStatus === GoogleMapsLibraryStatus.SUCCESS) {
			setMap(new google.maps.Map(ref.current, mapConfig));
			setAutoComplete(new google.maps.places.Autocomplete(searchInputRef.current));
		}

		return () => {
			setMap(null);
			setAutoComplete(null);
			setSelectedStoreMarker(null);
			setStores([]);
		};
	}, [libraryStatus]);

	useEffect(() => {
		if (!map) {
			return;
		}

		map.addListener('dragend', () => setTimeout(() => setReloadStores(true), 500));
		map.addListener('zoom_changed', () => setTimeout(() => setReloadStores(true), 500));
	}, [map]);

	useEffect(() => {
		if (autoComplete) {
			autoComplete.addListener('place_changed', () => {
				const place = autoComplete.getPlace();

				if (!place) {
					searchInputRef.current.value = null;

					return;
				}

				setPlaceChanged(true);
				map.fitBounds(place.geometry.viewport);
				map.setCenter(place.geometry.location);

				if (place.types.includes('country')) {
					// To reload stores
					setReloadStores(true);
				} else {
					map.setZoom(STORE_LOCATOR_ZOOM_LOCATION);
				}
			});
		}
	}, [autoComplete]);

	useEffect(() => {
		if (!map) {
			return;
		}

		map.setCenter(center);
		map.setZoom(map.getZoom());
	}, [center]);

	useEffect(() => {
		if (reloadStores) {
			setSelectedStoreMarker(null);
			getStores({...map.getCenter().toJSON()});
		}
	}, [reloadStores]);

	return (
		<>
			<div
				ref={ref}
				id="map"
				className={cn(['store-locator-map', {'store-locator-map--visible': visible}])}
			></div>

			{stores.map((store) => (
				<Marker
					key={`marker-${store.ID}-${currentLanguageCode}`}
					map={map}
					store={store}
					position={{lat: store.latitude, lng: store.longitude}}
					clickCallback={handleMarkerClick}
				/>
			))}
			<InfoWindowWrapper hasStorePage={true} />
		</>
	);
};

export default StoreLocatorMap;
